import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { GuaranteedPrizeSection } from '@/feature/betslip-pickem/components/GuaranteedPrizeSection';
import { LabelDividerRow } from '@/feature/betslip-pickem/components/LabelDividerRow';
import { PayoutLine } from '@/feature/betslip-pickem/components/PayoutLine';
import { useBetslipData } from '@/feature/betslip-pickem/hooks/use-betslip-data';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import {
    getBoostedMultiplierDetails,
    hasMultiplierExceededError,
    isFreeEntryPromo,
} from '@/feature/betslip-pickem/utils/betslip-utils';
import { GameMode, GameType } from '@/types/api.generated';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { useMysteryMode } from '../hooks/use-mystery-mode';

export const PayoutBreakdown = () => {
    const {
        dynamicBoostedMultiplier,
        perfectRegularMultiplier,
        perfectBoostedMultiplier,
        mysteryMultipliers,
        betslip,
        dynamicMultipliers,
        pickslipEntryRules,
    } = useBetslipData();
    const { mysteryApplied, mysteryToWin } = useMysteryMode();
    const gameMode = useBetslipStore(state => state.activeGameMode);
    const entryAmount = useBetslipStore(state => state.entryAmount);
    const { t } = useTranslation('betslip_pickem');
    const validationData = useBetslipStore(state => state.validationData[gameMode]);
    const { p2pGuaranteedPrize, p2pGuaranteedPrizeAmount } = validationData?.betValidation ?? {};
    const perfectToWin = useBetslipStore(state => state.validationData.PERFECT?.betValidation?.toWin);
    const freeEntryApplied = useBetslipStore(state => isFreeEntryPromo(state.selectedPromo));

    const rule = pickslipEntryRules.find(entryRule => entryRule?.gameMode === gameMode);
    const p2pGameType = rule?.gameType === GameType.P2p;

    const betslipLength = betslip.length;
    const guaranteedPrizeLabel =
        freeEntryApplied && p2pGuaranteedPrizeAmount
            ? toLocaleCurrency(p2pGuaranteedPrizeAmount)
            : p2pGuaranteedPrize
            ? `${p2pGuaranteedPrize}x`
            : null;
    const hidePayoutDetails = hasMultiplierExceededError(validationData?.betValidation);
    const showDynamicPayout = p2pGameType && mysteryApplied;

    return (
        <Box
            borderRadius={'r16'}
            backgroundColor={ds.colorToken.background.neutral.subtler}
            borderWidth={1}
            style={styles.container}
        >
            <Text variant={'titleLarge'} color={ds.colorToken.text.primary} paddingBottom={'s8'}>
                {t('payout_breakdown')}
            </Text>
            <LabelDividerRow
                leftText={p2pGameType ? t('minimum_guarantee') : t('ways_to_win')}
                rightText={t('payout')}
            />
            {gameMode === GameMode.Perfect ? (
                <>
                    <PayoutLine
                        description={t('number_of_picks_correct', { winningPicks: betslipLength, betslipLength })}
                        winningAmount={mysteryApplied ? mysteryToWin : perfectToWin}
                        multiplier={perfectRegularMultiplier}
                        boostedMultiplier={perfectBoostedMultiplier}
                        mysteryMultipliers={mysteryApplied ? mysteryMultipliers : undefined}
                        dynamicPayout={showDynamicPayout}
                        amount={entryAmount}
                        hidePayoutDetails={hidePayoutDetails}
                        hideMultiplier={freeEntryApplied}
                        dollarSignsNumber={3}
                        activeDollarSignsNumber={3}
                    />
                    {p2pGameType && guaranteedPrizeLabel ? (
                        <GuaranteedPrizeSection
                            description={t('p2p_play_description')}
                            prizeLabel={guaranteedPrizeLabel}
                        />
                    ) : null}
                </>
            ) : gameMode === GameMode.Dynamic && dynamicMultipliers.DYNAMIC ? (
                <>
                    {dynamicMultipliers.DYNAMIC.sort((a, b) => b.winningPicks - a.winningPicks).map(
                        (dynamicMultiplier, index) => {
                            // only the highest multiplier can get boosted, so we only show the boosted multiplier for the first one
                            const { boostedMultiplier, toWin } = getBoostedMultiplierDetails(
                                index === 0,
                                dynamicMultiplier,
                                dynamicBoostedMultiplier
                            );
                            const dollarCount = dynamicMultipliers.DYNAMIC.length >= 4 ? 4 : 3;
                            const filledCount = Math.max(0, dollarCount - index);
                            return (
                                <PayoutLine
                                    key={`payout-line${dynamicMultiplier.multiplier}-${index}`}
                                    description={t('number_of_picks_correct', {
                                        winningPicks: dynamicMultiplier.winningPicks,
                                        betslipLength: betslipLength,
                                    })}
                                    winningAmount={toWin}
                                    multiplier={dynamicMultiplier.multiplier}
                                    boostedMultiplier={boostedMultiplier}
                                    amount={entryAmount}
                                    hidePayoutDetails={hidePayoutDetails}
                                    hideMultiplier={freeEntryApplied}
                                    activeDollarSignsNumber={filledCount}
                                    dollarSignsNumber={dollarCount}
                                />
                            );
                        }
                    )}
                    {p2pGameType && guaranteedPrizeLabel ? (
                        <GuaranteedPrizeSection
                            description={t('p2p_play_description')}
                            prizeLabel={guaranteedPrizeLabel}
                        />
                    ) : null}
                </>
            ) : null}
        </Box>
    );
};

const styles = StyleSheet.create({
    container: {
        borderColor: ds.colorValue.border.subtle,
        paddingHorizontal: 16,
        paddingTop: 15,
        paddingBottom: 19,
    },
});
